import {Component} from '@angular/core';
import {Router} from '@angular/router';
import {DashboardDataService} from '../services/dashboard-data.service';
import {ProjectStats} from '../interfaces/projectStats';
import {CommonModule, DecimalPipe} from '@angular/common';
import * as L from 'leaflet';
import {HttpClientModule} from '@angular/common/http';
import {SchoolLocations} from '../data/locations';
import {SchoolLocation} from './schoolLocation.model';
import {FormsModule} from '@angular/forms';
import {NgChartsModule} from 'ng2-charts';
import {ChartConfiguration, ChartOptions} from 'chart.js';
import {TeacherService} from "../services/teacher.service";
import {Teacher} from "../interfaces/teacher";
import {AnnualSchoolProfileStats} from "../interfaces/annual-school-profile-stats";
import {DashboardAnnualSectionComponent} from "./cards/annual-section/dashboard-annual-section.component";
import {DashboardProjectSectionComponent} from "./cards/project-section/dashboard-project-section.component";

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  imports: [
    CommonModule, DecimalPipe, HttpClientModule,
    FormsModule, NgChartsModule,
    DashboardAnnualSectionComponent,
    DashboardProjectSectionComponent
  ],
  standalone: true,
})
export class DashboardComponent {
  constructor(
    private router: Router,
    private dashboardDataService: DashboardDataService,
    private teacherService: TeacherService
  ) {
  }

  Math = Math;

  // Teacher Info
  teacher: Teacher | null = null;
  teacherRole?: string = '';
  teacherSchool?: string = '';

  // Dashboard Data
  dashboardData?: ProjectStats;
  dashTest?: AnnualSchoolProfileStats;
  annualRatioData!: ChartConfiguration['data'];
  projectGrowthByYearData!: ChartConfiguration['data'];
  selectedYear?: number[] = [];
  yearArray: number[] = [];
  projectGrowthByYearOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {position: 'top'},
      title: {display: true, text: 'Project Growth Over Time'},
    },
    scales: {
      y: {beginAtZero: true, title: {display: true, text: 'Students Participating'}},
      x: {title: {display: true, text: 'Year'}},
    },
  };

  // Map and Location Data
  map!: L.Map;
  mapInitialized = false;
  defaultCoordinates: [number, number] = [33.9508, -83.9876];
  cityMarkers: Record<string, L.Marker> = {};
  cityToSchools: Record<string, SchoolLocation[]> = {};
  clusters: Record<string, SchoolLocation[]> = {};
  schools: Record<string, SchoolLocation[]> = {};
  selectedMapCity?: string;
  currentMapCity?: string;
  selectedSchool?: string;
  selectedClusters: string[] = [];
  selectedSchools: string[] = [];

  // Pagination
  currentTablePage = 0;
  paginatedPages: any[] = [];

  // Project Stats
  totalProject = 0;
  totalSchool = 0;
  percentageOfSchoolParticipatingInProject = 0;
  totalStudentsInFoodWasteProject = 0;
  totalTeachersInFoodWasteProject = 0;
  totalStudentsInGardenProject = 0;
  totalTeachersInGardenProject = 0;
  totalStudentsInWaterProject = 0;
  totalTeachersInWaterProject = 0;
  totalSchoolParticipatingInProject = 0;
  foodWasteSchoolParticipation = 0;
  gardenSchoolParticipation = 0;
  waterSchoolParticipation = 0;
  foodWasteParticipationPercentage = 0;
  gardenParticipationPercentage = 0;
  waterParticipationPercentage = 0;

  // Annual Stats
  totalAnnualStudents = 0;
  totalAnnualSchools = 0;
  totalAnnualStaffs = 0;
  annualRatio = '';
  annualRatioOption: any;
  totalAnnualFoodWaste = 0;
  totalAnnualGardens = 0;
  totalAnnualWater = 0;
  totalAnnualRecycling = 0;
  totalAnnualEnergy = 0;
  totalAnnualOther = 0;

  // Section
  activeSection = 'overview';

  // Search
  searchTerm = '';

  dashboardSchoolArray: any[] = [];
  impersonatedTeacher: Teacher | null = null;

  get activeRole() {
    return this.impersonatedTeacher?.role ?? this.teacherRole;
  }

  get activeSchool() {
    return this.impersonatedTeacher?.school ?? this.teacherSchool;
  }

  get schoolParticipationArray() {
    return this.paginatedPages[this.currentTablePage] || [];
  }

  /** SEARCH */
  goToSearch() {
    this.router.navigate(['/dashboard-search-result'], {queryParams: {q: this.searchTerm}});
  }

  /** NG */
  async ngOnInit() {
    this.teacher = await this.teacherService.getTeacher();
    if (this.isClusterLocked && this.teacherSchool) {
      const loc = SchoolLocations.find(s => s.SchoolName === this.teacherSchool);
      if (loc) {
        this.selectedClusters = [loc.Cluster];
        this.selectedSchools = (this.clusters[loc.Cluster] || []).map(s => s.SchoolName);
      }
    }

    this.teacherRole = this.teacher?.role;
    this.teacherSchool = this.teacher?.school;
    await this.loadTeacherReps();
    await this.loadTeachers();

    this.selectedYear = [];
    this.loadProjectData();
    this.loadAnnualProfileData();

    // set as the current year
    this.selectedYear = [new Date().getFullYear()];
    this.loadProjectData();
    this.loadAnnualProfileData();

    this.dashboardDataService.getSchoolData(this.activeRole, this.activeSchool)
      .subscribe((data: any) => {
        this.totalSchool = data.totalSchools;

        // Build a merged school object so template + filtering use consistent fields
        this.dashboardSchoolArray = (data.schools || []).map((apiSchool: any) => {
          const apiName = (apiSchool.name || apiSchool.SchoolName || '').trim();
          // find location by name match ignoring the "School" behind
          const loc = SchoolLocations.find((l: any) =>
            l.SchoolName.replace(/\s*School$/i, '').trim().toLowerCase()
            === apiName.replace(/\s*School$/i, '').trim().toLowerCase()
          );

          return {
            SchoolName: loc?.SchoolName || apiName || apiSchool.label || '',
            apiId: apiSchool.id ?? apiSchool._id ?? null,
            Cluster: loc?.Cluster || '',
            CityAndState: loc?.CityAndState || apiSchool.city || apiSchool.CityAndState || '',
            studentCount: apiSchool.studentCount ?? apiSchool.students ?? apiSchool.studentsCount ?? apiSchool.enrollment ?? 0,
            Lat: loc?.Lat ?? null,
            Lng: loc?.Lng ?? null,
            _api: apiSchool,
            _loc: loc
          };
        })
          .filter((s: any) => s && s.SchoolName);

        // Build cityToSchools/clusters/schools from merged array so filters/map align with table
        this.cityToSchools = {};
        this.clusters = {};
        this.schools = {};
        this.dashboardSchoolArray.forEach((s: any) => {
          if (s.CityAndState) {
            this.cityToSchools[s.CityAndState] = this.cityToSchools[s.CityAndState] || [];
            this.cityToSchools[s.CityAndState].push(s);
          }
          if (s.Cluster) {
            this.clusters[s.Cluster] = this.clusters[s.Cluster] || [];
            this.clusters[s.Cluster].push(s);
          }
          if (s.SchoolName) {
            this.schools[s.SchoolName] = this.schools[s.SchoolName] || [];
            this.schools[s.SchoolName].push(s);
          }
        });

        // Refresh pagination + markers
        this.updateSchoolPagination();
        this.createCityMarkers();

        this.percentageOfSchoolParticipatingInProject =
          this.totalSchool ? parseFloat(((this.totalSchoolParticipatingInProject / this.totalSchool) * 100).toFixed(2)) : 0;
      });
  }

  ngAfterViewChecked() {
    if (this.activeSection === 'overview' && !this.mapInitialized) {
      this.initMap();
      this.mapInitialized = true;
    }
  }

  /** MAP */
  initMap() {
    if (this.map) {
      this.map.invalidateSize();
      return;
    }

    this.map = L.map('map').setView(this.defaultCoordinates, 11);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap'
    }).addTo(this.map);

    Object.entries(this.cityToSchools).forEach(([city, locations]) => {
      if (!this.cityMarkers[city]) {
        const marker = L.marker([locations[0].Lat, locations[0].Lng]).addTo(this.map);
        this.cityMarkers[city] = marker;
        marker.on('click', () => this.onCityClick(city, locations[0]));
      }
    });

    setTimeout(() => this.map.invalidateSize(), 200);
  }

  onCityClick(city: string, loc: { Lat: number; Lng: number }) {
    this.selectedMapCity = this.currentMapCity === city ? undefined : city;
    this.currentMapCity = this.selectedMapCity;

    Object.entries(this.cityMarkers).forEach(([c, m]) => {
      const showMarker =
        (!this.selectedClusters.length || this.selectedClusters.some(cluster => (this.clusters[cluster] || []).some(s => s.CityAndState === c))) &&
        (!this.selectedSchools.length || this.selectedSchools.some(school => (this.schools[school] || []).some(s => s.CityAndState === c))) &&
        (!this.selectedMapCity || c === this.selectedMapCity);

      showMarker ? m.addTo(this.map) : this.map.removeLayer(m);
    });

    if (this.selectedMapCity) this.map.setView([loc.Lat, loc.Lng], 15, {animate: true});
    else this.map.setView(this.defaultCoordinates, 11, {animate: true});

    this.updateSchoolPagination();
  }

  /** PAGINATION */
  updateSchoolPagination() {
    if (!this.dashboardSchoolArray || !Array.isArray(this.dashboardSchoolArray)) {
      this.paginatedPages = [];
      this.currentTablePage = 0;
      return [];
    }

    let filteredSchools = [...this.dashboardSchoolArray];

    if (this.selectedClusters.length) {
      const clusterSet = new Set(this.selectedClusters.map(c => c.toLowerCase()));
      filteredSchools = filteredSchools.filter(school => (school.Cluster || '').toLowerCase() && clusterSet.has((school.Cluster || '').toLowerCase()));
    }

    if (this.selectedSchools.length) {
      const selectedSet = new Set(this.selectedSchools.map(s => s.toLowerCase()));
      filteredSchools = filteredSchools.filter(school => selectedSet.has((school.SchoolName || '').toLowerCase()));
    }

    if (this.selectedMapCity) {
      const citySet = new Set([this.selectedMapCity]);
      filteredSchools = filteredSchools.filter(school => citySet.has(school.CityAndState));
    }

    const chunkSize = 8;
    this.paginatedPages = [];
    for (let i = 0; i < filteredSchools.length; i += chunkSize) {
      this.paginatedPages.push(filteredSchools.slice(i, i + chunkSize));
    }

    this.currentTablePage = this.paginatedPages.length === 0 ? 0 : Math.min(Math.max(this.currentTablePage, 0), this.paginatedPages.length - 1);
    this.createCityMarkers(filteredSchools);

    return this.paginatedPages[this.currentTablePage] || [];
  }

  prevPage() {
    this.currentTablePage = Math.max(this.currentTablePage - 1, 0);
  }

  nextPage() {
    this.currentTablePage = Math.min(this.currentTablePage + 1, this.paginatedPages.length - 1);
  }

  goToPage(p: number) {
    this.currentTablePage = p;
  }

  /**FILTERS */
  get clusterDropdownText() {
    return !this.selectedClusters.length ? 'All Clusters' :
      this.selectedClusters.length === 1 ? this.selectedClusters[0] :
        `${this.selectedClusters.length} Clusters`;
  }

  get clusterArray() {
    return Object.keys(this.clusters).sort();
  }

  get schoolDropdownText() {
    return !this.selectedSchools.length ? 'All Schools' :
      this.selectedSchools.length === 1 ? this.selectedSchools[0] :
        `${this.selectedSchools.length} Schools`;
  }

  get schoolArray() {
    let schoolsToShow = Object.keys(this.schools);
    if (this.selectedClusters.length) {
      const clusterSchools = new Set<string>();
      this.selectedClusters.forEach(c => (this.clusters[c] || []).forEach(s => clusterSchools.add(s.SchoolName)));
      schoolsToShow = schoolsToShow.filter(s => clusterSchools.has(s));
    }
    return schoolsToShow.sort();
  }

  get yearDropdownText() {
    return !this.selectedYear?.length ? 'Year' :
      this.selectedYear.length === 1 ? this.selectedYear[0] :
        `${this.selectedYear.length} Years`;
  }

  onClusterCheckedChanged(event: Event, cluster: string) {
    const checked = (event.target as HTMLInputElement).checked;

    // update selected clusters
    this.selectedClusters = checked
      ? [...this.selectedClusters, cluster]
      : this.selectedClusters.filter(c => c !== cluster);

    // if no clusters selected keep everything
    if (!this.selectedClusters.length) {
      this.updateSchoolPagination();
      this.loadProjectData();
      this.loadAnnualProfileData();
      return;
    }

    const allowedSchools = new Set<string>();
    this.selectedClusters.forEach(c => {
      (this.clusters[c] || []).forEach(s =>
        allowedSchools.add(s.SchoolName)
      );
    });

    // keep only schools that are from cluster
    this.selectedSchools = this.selectedSchools.filter(s =>
      allowedSchools.has(s)
    );

    this.updateSchoolPagination();
    this.loadProjectData();
    this.loadAnnualProfileData();
  }

  onSchoolCheckedChanged(event: Event, school: string) {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedSchools = checked
      ? [...this.selectedSchools, school]
      : this.selectedSchools.filter(s => s !== school);

    this.updateSchoolPagination();
    this.loadProjectData();
    this.loadAnnualProfileData();
  }

  onYearCheckedChanged(event: Event, year: number) {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedYear = checked
      ? [...(this.selectedYear || []), year]
      : this.selectedYear?.filter(y => y != year);

    this.loadAnnualProfileData();
    this.loadProjectData();
  }

  onSchoolRowClicked(schoolName: string) {
    if (this.selectedSchool === schoolName) {
      this.selectedSchool = undefined;
      this.selectedClusters = [];
    } else this.selectedSchool = schoolName;

    const loc = SchoolLocations.find(s => s.SchoolName === schoolName || s.SchoolName.replace(/ School$/, '') === schoolName);
    if (loc) this.map.setView([loc.Lat, loc.Lng], 15, {animate: true});

    this.updateSchoolPagination();
  }

  /** DATA LOAD */
  loadProjectData() {
    let teacherCluster: string | null = null;
    const activeTeacher = this.impersonatedTeacher || this.teacher;
    if (activeTeacher?.role === 'Teacher' || activeTeacher?.role === 'GHS Teacher Representative') {
      teacherCluster = SchoolLocations.find(s => s.SchoolName === activeTeacher.school)?.Cluster || null;
      this.selectedClusters = teacherCluster ? [teacherCluster] : [];
    }

    this.clusters = {};
    this.schools = {};
    this.cityToSchools = {};

    SchoolLocations.forEach(location => {
      if (teacherCluster && location.Cluster !== teacherCluster) return;

      this.cityToSchools[location.CityAndState] = this.cityToSchools[location.CityAndState] || [];
      this.cityToSchools[location.CityAndState].push(location);

      this.clusters[location.Cluster] = this.clusters[location.Cluster] || [];
      this.clusters[location.Cluster].push(location);

      this.schools[location.SchoolName] = this.schools[location.SchoolName] || [];
      this.schools[location.SchoolName].push(location);
    });

    this.dashboardDataService.getProjectsData(this.activeRole, this.activeSchool, this.selectedClusters, this.selectedSchools, this.selectedYear)
      .subscribe(data => {
        this.dashboardData = data;
        this.updateSchoolPagination();

        this.totalProject = data.totalProject;
        this.totalStudentsInFoodWasteProject = data.totalStudentsInFoodWaste;
        this.totalTeachersInFoodWasteProject = data.totalTeachersInFoodWaste;
        this.totalStudentsInGardenProject = data.totalStudentsInGarden;
        this.totalTeachersInGardenProject = data.totalTeachersInGarden;
        this.totalStudentsInWaterProject = data.totalStudentsInWater;
        this.totalTeachersInWaterProject = data.totalTeachersInWater;
        this.totalSchoolParticipatingInProject = data.participatingSchools;
        this.foodWasteSchoolParticipation = data.schoolParticipationCounts.foodWaste;
        this.gardenSchoolParticipation = data.schoolParticipationCounts.garden;
        this.waterSchoolParticipation = data.schoolParticipationCounts.water;
        this.foodWasteParticipationPercentage = data.projectParticipationPercentage.foodWaste;
        this.gardenParticipationPercentage = data.projectParticipationPercentage.garden;
        this.waterParticipationPercentage = data.projectParticipationPercentage.water;

        this.createCityMarkers();
        if (this.activeSection === 'overview') this.initMap();

        const years = Array.from(new Set([
          ...Object.keys(data.studentsInFoodWasteByYear || {}),
          ...Object.keys(data.studentsInGardenByYear || {}),
          ...Object.keys(data.studentsInWaterByYear || {}),
        ])).sort();

        this.projectGrowthByYearData = {
          labels: years,
          datasets: [
            {
              data: years.map(y => data.studentsInFoodWasteByYear?.[+y] || 0),
              label: 'Food Waste',
              borderColor: '#f87171',
              backgroundColor: 'rgba(248,113,113,0.2)',
              fill: true,
              tension: 0.3
            },
            {
              data: years.map(y => data.studentsInGardenByYear?.[+y] || 0),
              label: 'Garden',
              borderColor: '#34d399',
              backgroundColor: 'rgba(52,211,153,0.2)',
              fill: true,
              tension: 0.3
            },
            {
              data: years.map(y => data.studentsInWaterByYear?.[+y] || 0),
              label: 'Water',
              borderColor: '#60a5fa',
              backgroundColor: 'rgba(96,165,250,0.2)',
              fill: true,
              tension: 0.3
            }
          ]
        };
        this.updateAvailableYears(data.years);
      });
  }

  createCityMarkers(filtered?: any[]) {
    // Use filtered school, otherwise use dashboard array
    const schoolsToMap = filtered || this.dashboardSchoolArray;

    // Apply current filters for clusters and schools
    const filteredSchools = schoolsToMap.filter(school => {
      const clusterMatch = !this.selectedClusters.length || this.selectedClusters.includes(school.Cluster);
      const schoolMatch = !this.selectedSchools.length || this.selectedSchools.includes(school.SchoolName);
      return clusterMatch && schoolMatch;
    });

    // Clear existing markers
    Object.values(this.cityMarkers).forEach(m => {
      if (this.map?.hasLayer(m)) this.map.removeLayer(m);
    });
    this.cityMarkers = {};

    // Recreate markers for filtered schools
    const grouped: Record<string, any[]> = {};
    filteredSchools.forEach(s => {
      if (!s.CityAndState || !s.Lat || !s.Lng) return;
      grouped[s.CityAndState] = grouped[s.CityAndState] || [];
      grouped[s.CityAndState].push(s);
    });

    Object.entries(grouped).forEach(([city, locs]) => {
      const {Lat, Lng} = locs[0];
      const marker = L.marker([Lat, Lng]).addTo(this.map);
      marker.on('click', () => this.onCityClick(city, {Lat, Lng}));
      this.cityMarkers[city] = marker;
    });
  }

  loadAnnualProfileData() {
    this.dashboardDataService.getAnnualSchoolProfileData(this.activeRole, this.activeSchool, this.selectedClusters, this.selectedSchools, this.selectedYear)
      .subscribe(data => {
        this.dashTest = data;
        this.totalAnnualStudents = data.totalStudents;
        this.totalAnnualSchools = data.totalSchools;
        this.totalAnnualStaffs = data.totalStaffs;
        this.annualRatio = data.studentRatio + ' : ' + data.staffRatio;
        this.totalAnnualFoodWaste = data.totalFoodWaste;
        this.totalAnnualGardens = data.totalGardens;
        this.totalAnnualWater = data.totalWater;
        this.totalAnnualRecycling = data.totalRecycling;
        this.totalAnnualEnergy = data.totalEnergy;
        this.totalAnnualOther = data.totalOther;

        const schools = data.schools.map(s => s.name);
        this.annualRatioData = {
          labels: schools,
          datasets: [
            {
              label: 'Students',
              data: data.schools.map(s => s.students),
              backgroundColor: 'rgb(71 103 2)',
              borderColor: 'rgb(71 103 2)'
            },
            {
              label: 'Teachers',
              data: data.schools.map(s => s.teachers),
              backgroundColor: 'rgb(132 204 21)',
              borderColor: 'rgb(132 204 21)'
            }
          ]
        };
        this.annualRatioOption = {
          plugins: {legend: {labels: {color: 'rgb(0 0 0)', font: {size: 10}}}},
          scales: {
            x: {ticks: {color: 'rgb(0 0 0)', font: {size: 12}}},
            y: {ticks: {color: 'rgb(0 0 0)', font: {size: 12}}}
          }
        };

        this.updateAvailableYears(data.years);

      });
  }

  setActive(section: string) {
    this.activeSection = section;
    if (section === 'overview' && this.map) setTimeout(() => this.map.invalidateSize(), 0);
  }

  trackBySchool(index: number, item: any) {
    return item[0];
  }

  removeGASuffix(name: string): string {
    if (!name) return '';
    return name.replace(/\s*GA$/, '');
  }

  private updateAvailableYears(newYears: number[]) {
    const merged = new Set([...this.yearArray, ...newYears]);
    this.yearArray = Array.from(merged).sort((a, b) => b - a);
  }

  clearFilters() {
    this.selectedClusters = [];
    this.selectedSchools = [];
    this.selectedYear = [];

    this.selectedMapCity = undefined;
    this.currentMapCity = undefined;
    this.selectedSchool = undefined;

    this.impersonatedTeacher = null;
    this.teacherRepDropdownText = 'Teacher Representative';
    this.teacherDropdownText = 'Teacher';

    this.updateSchoolPagination();
    this.loadProjectData();
    this.loadAnnualProfileData();
  }

  teacherRepDropdownText = 'Teacher Representative';
  teacherDropdownText = 'Teacher';
  teacherReps: Teacher[] = [];
  teachers: Teacher[] = [];

  loadTeacherReps() {
    this.teacherService.getTeachersByExactRole('GHS Teacher Representative')
      .then(t => this.teacherReps = t || []);
    console.log("q"+this.teacherReps);
  }

  loadTeachers() {
    this.teacherService.getTeachersByExactRole('Teacher')
      .then(t => this.teachers = t || []);
  }

  selectTeacher(teacher: Teacher) {
    this.impersonatedTeacher = teacher;
    this.teacherRepDropdownText = teacher.role === 'GHS Teacher Representative'
      ? `${teacher.fName} ${teacher.lName}`
      : this.teacherRepDropdownText;
    this.teacherDropdownText = teacher.role === 'Teacher'
      ? `${teacher.fName} ${teacher.lName}`
      : this.teacherDropdownText;

    this.selectedClusters = [];
    this.selectedSchools = [];

    // reload dashboard for the selected teacher
    this.loadProjectData();
    this.loadAnnualProfileData();
  }

  get isClusterLocked() {
    return this.teacherRole === 'Teacher' || this.teacherRole === 'GHS Teacher Representative';
  }
}
